import { useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIONI = [
    "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
    "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
    "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
    "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];
const TIPOLOGIE = [
    { value: "ristorazione", tKey: "reg.tip.ristorazione" },
    { value: "alloggi", tKey: "reg.tip.alloggi" },
    { value: "esperienze", tKey: "reg.tip.esperienze" },
    { value: "altro", tKey: "reg.tip.altro" },
];

const initial = {
    referente: "", attivita: "", tipologia: "ristorazione",
    citta: "", regione: "Marche", email: "", telefono: "",
    messaggio: "", consenso_privacy: false,
};

const STEP_KEYS = [
    { key: "attivita", tKey: "reg.step.attivita" },
    { key: "contatti", tKey: "reg.step.contatti" },
    { key: "conferma", tKey: "reg.step.conferma" },
];

export default function RegisterForm() {
    const { t } = useI18n();
    const [form, setForm] = useState(initial);
    const [step, setStep] = useState(0);
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const canGoNext = () => {
        if (step === 0) return form.attivita.trim().length >= 2 && form.tipologia && form.regione && form.citta.trim().length >= 2;
        if (step === 1) return form.referente.trim().length >= 2 && form.email.includes("@") && form.telefono.trim().length >= 6;
        if (step === 2) return form.consenso_privacy;
        return false;
    };

    const submit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        if (!canGoNext()) return;
        setStatus("loading");
        try {
            await axios.post(`${API}/partners`, form);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            const detail = err?.response?.data?.detail || t("reg.errorGeneric");
            setErrorMsg(typeof detail === "string" ? detail : t("reg.errorRequest"));
        }
    };

    const tipologiaLabel = (val) => {
        const tip = TIPOLOGIE.find((x) => x.value === val);
        return tip ? t(tip.tKey) : val;
    };

    if (status === "success") {
        return (
            <section id="candidati" data-testid="register-section" style={{ background: "var(--itly-teal-050)", padding: "96px 32px" }}>
                <div className="itly-card" data-testid="register-success" style={{ maxWidth: 640, margin: "0 auto", padding: "56px 40px", textAlign: "center" }}>
                    <div style={{ width: 56, height: 56, borderRadius: 999, background: "var(--itly-teal-050)", color: "var(--itly-fg-strong)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20, border: "1px solid var(--itly-border)" }} aria-hidden="true">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                    </div>
                    <h3 className="itly-serif" style={{ margin: 0, fontSize: 32, color: "var(--itly-fg-strong)", letterSpacing: "-0.02em" }}>
                        {t("reg.success.title")}
                    </h3>
                    <p style={{ marginTop: 16, fontFamily: "Inter, sans-serif", fontSize: 16, lineHeight: 1.6, color: "var(--itly-muted)", maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
                        {t("reg.success.body")}
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section id="candidati" data-testid="register-section" style={{ background: "var(--itly-teal-050)", padding: "96px 32px" }} data-reveal="up">
            <div style={{ maxWidth: 1080, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--itly-orange-700)", margin: 0 }}>
                        {t("reg.badge")}
                    </p>
                    <h2 className="itly-serif" style={{ marginTop: 14, marginBottom: 0, fontSize: "clamp(32px, 3.4vw, 46px)", lineHeight: 1.1, color: "var(--itly-fg-strong)", letterSpacing: "-0.02em" }}>
                        {t("reg.title_a")}{" "}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("reg.title_b")}
                        </em>
                    </h2>
                </div>

                {/* Progress */}
                <div data-testid="register-progress" style={{ maxWidth: 560, margin: "0 auto 24px", display: "flex", alignItems: "center", gap: 8 }}>
                    {STEP_KEYS.map((s, i) => (
                        <div key={s.key} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                                data-testid={`register-step-indicator-${i}`}
                                style={{
                                    width: 28, height: 28, borderRadius: 999,
                                    background: i <= step ? "var(--itly-teal-900)" : "var(--itly-surface)",
                                    border: "1px solid " + (i <= step ? "var(--itly-teal-900)" : "var(--itly-border)"),
                                    color: i <= step ? "#F7F4EE" : "var(--itly-muted)",
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 13,
                                    flexShrink: 0, transition: "all 220ms ease",
                                }}
                            >
                                {i < step ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg> : i + 1}
                            </span>
                            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: i <= step ? "var(--itly-fg-strong)" : "var(--itly-muted)", fontWeight: 600, letterSpacing: "0.02em" }}>
                                    {t(s.tKey)}
                                </span>
                                {i < STEP_KEYS.length - 1 && (
                                    <span style={{ height: 2, background: i < step ? "var(--itly-teal-900)" : "var(--itly-border)", marginTop: 6, transition: "background 220ms ease" }} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="itly-register-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: 24, alignItems: "start" }}>
                    <form onSubmit={submit} data-testid="register-form" className="itly-card" style={{ padding: "32px 30px", display: "flex", flexDirection: "column", gap: 18 }} noValidate>
                        {step === 0 && (
                            <>
                                <Field label={t("reg.f.attivita")} required>
                                    <input name="attivita" value={form.attivita} onChange={onChange} required className="itly-input" data-testid="input-attivita" placeholder={t("reg.f.attivita.ph")} />
                                </Field>
                                <Field label={t("reg.f.tipologia")} required>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {TIPOLOGIE.map((tip) => (
                                            <button key={tip.value} type="button" onClick={() => setForm((f) => ({ ...f, tipologia: tip.value }))} className={`itly-chip-light ${form.tipologia === tip.value ? "on" : ""}`} data-testid={`input-tipologia-${tip.value}`}>
                                                {t(tip.tKey)}
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label={t("reg.f.regione")} required>
                                    <select name="regione" value={form.regione} onChange={onChange} required className="itly-input" data-testid="input-regione">
                                        {REGIONI.map((r) => (<option key={r} value={r}>{r}</option>))}
                                    </select>
                                </Field>
                                <Field label={t("reg.f.citta")} required>
                                    <input name="citta" value={form.citta} onChange={onChange} required className="itly-input" data-testid="input-citta" placeholder={t("reg.f.citta.ph")} />
                                </Field>
                            </>
                        )}
                        {step === 1 && (
                            <>
                                <Field label={t("reg.f.referente")} required>
                                    <input name="referente" value={form.referente} onChange={onChange} required className="itly-input" data-testid="input-referente" placeholder={t("reg.f.referente.ph")} />
                                </Field>
                                <Field label={t("reg.f.email")} required>
                                    <input name="email" type="email" value={form.email} onChange={onChange} required className="itly-input" data-testid="input-email" placeholder={t("reg.f.email.ph")} />
                                </Field>
                                <Field label={t("reg.f.telefono")} required>
                                    <input name="telefono" value={form.telefono} onChange={onChange} required className="itly-input" data-testid="input-telefono" placeholder={t("reg.f.telefono.ph")} inputMode="tel" />
                                </Field>
                                <Field label={t("reg.f.messaggio")} helper={t("reg.f.messaggio.hint")}>
                                    <textarea name="messaggio" value={form.messaggio} onChange={onChange} className="itly-input" data-testid="input-messaggio" rows={3} style={{ resize: "vertical", minHeight: 80 }} />
                                </Field>
                            </>
                        )}
                        {step === 2 && (
                            <>
                                <div style={{ padding: "18px 20px", background: "var(--itly-teal-050)", borderRadius: 12, fontFamily: "Inter, sans-serif", fontSize: 14, color: "var(--itly-fg-strong)", lineHeight: 1.7 }}>
                                    <div><strong>{t("reg.confirm.attivita")}</strong> {form.attivita}</div>
                                    <div><strong>{t("reg.confirm.tipologia")}</strong> {tipologiaLabel(form.tipologia)}</div>
                                    <div><strong>{t("reg.confirm.dove")}</strong> {form.citta}, {form.regione}</div>
                                    <div><strong>{t("reg.confirm.referente")}</strong> {form.referente}</div>
                                    <div><strong>{t("reg.confirm.email")}</strong> {form.email}</div>
                                    <div><strong>{t("reg.confirm.telefono")}</strong> {form.telefono}</div>
                                </div>
                                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-muted)", lineHeight: 1.55, cursor: "pointer" }}>
                                    <input type="checkbox" name="consenso_privacy" checked={form.consenso_privacy} onChange={onChange} data-testid="input-consenso" style={{ marginTop: 4, accentColor: "var(--itly-teal-900)", cursor: "pointer" }} />
                                    <span>
                                        {t("reg.consenso")}
                                        <a href="/privacy" style={{ color: "var(--itly-fg-strong)", textDecoration: "underline" }}>{t("reg.consensoLink")}</a>
                                        {t("reg.consensoEnd")}
                                    </span>
                                </label>
                            </>
                        )}

                        {errorMsg && (
                            <div data-testid="register-error" style={{ padding: "12px 14px", borderRadius: 12, background: "var(--itly-surface-peach)", color: "var(--itly-orange-700)", border: "1px solid rgba(198, 90, 56, 0.25)", fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                                {errorMsg}
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, gap: 10 }}>
                            {step > 0 ? (
                                <button type="button" onClick={() => setStep((s) => s - 1)} className="itly-btn-secondary" data-testid="register-back">
                                    <span className="arrow" aria-hidden="true">←</span> {t("reg.back")}
                                </button>
                            ) : (
                                <span />
                            )}
                            {step < STEP_KEYS.length - 1 ? (
                                <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canGoNext()} className="itly-btn-primary" data-testid="register-next" style={{ opacity: canGoNext() ? 1 : 0.5 }}>
                                    {t("reg.next")} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                                </button>
                            ) : (
                                <button type="submit" disabled={status === "loading" || !canGoNext()} className="itly-btn-primary" data-testid="register-submit" style={{ opacity: canGoNext() ? 1 : 0.5 }}>
                                    {status === "loading" ? t("reg.submitting") : t("reg.submit")}
                                    {status !== "loading" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>}
                                </button>
                            )}
                        </div>
                    </form>

                    {/* Live preview */}
                    <div data-testid="register-preview" style={{ position: "sticky", top: 96 }}>
                        <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--itly-orange-700)", margin: "0 0 12px" }}>
                            {t("reg.preview.badge")}
                        </p>
                        <div className="itly-card" style={{ padding: "22px 22px 24px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--itly-teal-050)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--itly-fg-strong)", fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 20 }}>
                                    {(form.attivita || "?").trim().charAt(0).toUpperCase() || "?"}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 20, color: "var(--itly-fg-strong)", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {form.attivita || t("reg.preview.defaultName")}
                                    </div>
                                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-muted)" }}>
                                        {form.citta || t("reg.preview.defaultCity")} · {tipologiaLabel(form.tipologia)}
                                    </div>
                                </div>
                                <span style={{ padding: "3px 9px", background: "var(--itly-surface-green)", color: "var(--itly-green-ink)", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                    {t("reg.preview.verified")}
                                </span>
                            </div>
                            <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--itly-cream)", borderRadius: 10, fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-muted)", lineHeight: 1.55 }}>
                                <span style={{ color: "var(--itly-fg-strong)", fontWeight: 600 }}>{t("reg.preview.today")} </span>
                                <span style={{ color: "var(--itly-green-ink)", fontWeight: 600 }}>{t("reg.preview.quiet")}</span>
                                <span style={{ marginLeft: 10 }}>{t("reg.preview.open")}</span>
                            </div>
                            <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                {[t("reg.preview.tag1"), t("reg.preview.tag2"), t("reg.preview.tag3")].map((tag) => (
                                    <span key={tag} style={{ padding: "5px 10px", background: "var(--itly-peach-050)", color: "var(--itly-orange-700)", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 600 }}>
                                        {tag}
                                    </span>
                                ))}
                                <span style={{ padding: "5px 10px", background: "var(--itly-cream)", color: "var(--itly-muted)", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 600, border: "1px dashed var(--itly-border)" }}>
                                    {t("reg.preview.tag4")}
                                </span>
                            </div>
                        </div>
                        <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--itly-muted)", marginTop: 10, lineHeight: 1.5 }}>
                            {t("reg.preview.note")}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, required, helper, children }) {
    return (
        <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--itly-fg-strong)" }}>
                {label}{required && <span style={{ color: "var(--itly-orange-500)", marginLeft: 4 }}>*</span>}
            </span>
            {children}
            {helper && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-muted)", lineHeight: 1.5 }}>{helper}</span>}
        </label>
    );
}
